/**
 * Field guides in SCREEN space (CSS px of the stage), written by the tutorial engine once per rendered frame and
 * read by the world layer (ui/tutorial/WorldLayer.tsx) in its own rAF. A plain mutable object (not a signal): the
 * guides move every frame and must not re-render Preact. `version` increases on every write.
 */
import type { TeachCue } from './catalog';

export type GuideTone = 'gold' | 'green' | 'red' | 'blue' | 'white' | 'yellow';

export interface ScreenPoint {
  x: number;
  y: number;
  /** Inside the viewport (else the layer draws an edge pointer). */
  on: boolean;
}

/** A head on screen: the top of the head (`x`, `y`) and its radius (CSS px, 0 = unknown). */
export interface ScreenHead extends ScreenPoint {
  r: number;
}

export interface ScreenMarker {
  kind: 'cone' | 'zone' | 'spot' | 'flag';
  /** Screen x of the marker (zone: start). */
  x: number;
  /** Zone end. */
  x2: number;
  /** Turf y (screen). */
  y: number;
  label: string;
  /** Label of its edge pointer when off screen ('' = `label`). */
  edge: string;
  tone: GuideTone;
  done: boolean;
}

export interface ScreenArrow {
  x1: number;
  x2: number;
  y: number;
  label: string;
  tone: GuideTone;
}

export interface ScreenLine {
  /** Screen x of the painted line at the near sideline and at the far edge (perspective). */
  xNear: number;
  xFar: number;
  yNear: number;
  yFar: number;
  label: string;
  tone: GuideTone;
  pulse: boolean;
  /** -1 / 1: the line is off screen to that side (edge pointer), 0 = visible. */
  off: -1 | 0 | 1;
}

export interface TeachFrame {
  version: number;
  /** Guides are on (a lesson is running and the picture is live). */
  active: boolean;
  /** Stage size (CSS px) and px per yard on the player plane. */
  w: number;
  h: number;
  scale: number;
  /** Top of the human's head (the world bubble sits above it). */
  head: ScreenHead;
  /** Top of the opponent's head (the tell glyph). */
  opp: ScreenHead;
  you: boolean;
  cue: TeachCue | null;
  markers: ScreenMarker[];
  arrows: ScreenArrow[];
  lines: ScreenLine[];
  /**
   * `cue`: a throw's release cue — short of the target zone, in it (LET GO!), past it ('' = no target). `r` = the
   * ring's half width (CSS px): the real catch reach on the near side (kit.ts RING_NEAR), 0 = default size.
   */
  landing: (ScreenPoint & { ok: boolean; label: string; cue: '' | 'short' | 'go' | 'far'; r: number }) | null;
  thisWay: boolean;
  /**
   * Toss meter (0..1 fill, green band lo..hi as fractions) by the human's head, or null. `release` = where the
   * player let go (fraction), shown for a moment after the toss; -1 while charging.
   */
  meter: {
    fill: number;
    lo: number;
    hi: number;
    release: number;
    /** A toss through the posts or a throw at a target zone (the over-call: TOO MUCH! / TOO FAR!). */
    kind: 'toss' | 'throw';
    title: string;
  } | null;
  /** Teaching slow-mo vignette 0..1. */
  vignette: number;
}

export const createTeachFrame = (): TeachFrame => ({
  version: 0,
  active: false,
  w: 1,
  h: 1,
  scale: 1,
  head: { x: 0, y: 0, on: false, r: 0 },
  opp: { x: 0, y: 0, on: false, r: 0 },
  you: false,
  cue: null,
  markers: [],
  arrows: [],
  lines: [],
  landing: null,
  thisWay: false,
  meter: null,
  vignette: 0,
});
